import React, { useEffect, useRef, useState } from 'react';
import { useLocale } from 'locale';
import {
  LowerPriceEstimatedRange,
  HigherPriceEstimatedRange,
  ChartMarkerContainer,
  AdditionalInfoList,
  AdditionalInfoItem,
  EstimatedRange,
  EstimatedPoint,
  FullRange,
  Tooltip,
  Wrapper, Labels,
  MiddleLabel,
} from './styled';
import { SmallText } from 'ds/components/typography';
import { SpacingBlock } from 'components/spacing-block';
import { IPriceEstimationRecord } from 'store/state/selectors/insights/prices';
import { getEnforcedSiValue } from 'components/widget/prices/utils';
import { CurrentBulletinMarker } from '../styled';
import { useTheme } from 'hooks/useTheme';


const getPriceType = (downBoundaryPrice: number, topBoundaryPrice: number, estimatedPrice: number) => {
  if (estimatedPrice > topBoundaryPrice) return PriceOverviewType.High;
  if (estimatedPrice < downBoundaryPrice) return PriceOverviewType.Low;
  return PriceOverviewType.Typical;
};
const percentageToWidth = (n: number): string => `${n * 100}%`;

interface ChartProps {
  minPrice: number;
  maxPrice: number;
  minEstimation: number;
  maxEstimation: number;
  estimation: number;
  record: IPriceEstimationRecord;
  hideAdditionalInfo?: boolean;
}

export enum PriceOverviewType {
  Typical = 'typical',
  Low = 'low',
  High = 'high',
}

const ESTIMATED_WIDTH = .5;
const HIGHER_PRICES_WIDTH = .25;
const LOWER_PRICES_WIDTH = .25;

export enum TrianglePosition {
  START = 0,
  MIDDLE = 50,
  END = 100,
}

const Chart: React.FC<ChartProps> = ({
  minEstimation,
  maxEstimation,
  estimation,
  record,
  minPrice,
  maxPrice,
  hideAdditionalInfo,
}) => {
  const priceType: PriceOverviewType = record ? getPriceType(minEstimation, maxEstimation, record.price) : null;
  if (!priceType) return null;

  const markerPosition = (price: number): number => {
    if (price < minEstimation) {
      return (price - minPrice) / (minEstimation - minPrice) * LOWER_PRICES_WIDTH;
    }
    else if (price > maxEstimation) {
      return (price - maxEstimation) / (maxPrice - maxEstimation) * HIGHER_PRICES_WIDTH + LOWER_PRICES_WIDTH + ESTIMATED_WIDTH;
    }
    return (price - minEstimation) / estimatedRange * ESTIMATED_WIDTH + LOWER_PRICES_WIDTH;
  };

  const estimatedPositionInBlue = (estimation - minEstimation) / (maxEstimation - minEstimation);
  const estimatedRange = maxEstimation - minEstimation;
  const { t, formatMoneyWithMetricPrefix: fm } = useLocale();
  const { isRTL } = useTheme();

  const fullRangeRef = useRef(null);
  const [ trianglePosition, setTrianglePosition ] = useState(0);
  const [ tooltipOffset, setTooltipOffset ] = useState(0);

  useEffect(() => {
    if (!priceType || !record || !record.price) return;
    const pos = markerPosition(record.price);
    if (pos <= 0.4) {
      setTooltipOffset(isRTL ? TrianglePosition.END : TrianglePosition.START);
      setTrianglePosition(TrianglePosition.START);
    }
    else if (pos >= 0.6) {
      setTooltipOffset(isRTL ? TrianglePosition.START : TrianglePosition.END);
      setTrianglePosition(TrianglePosition.END);
    }
    else {
      setTooltipOffset(TrianglePosition.MIDDLE);
      setTrianglePosition(TrianglePosition.MIDDLE);
    }

  }, [ fullRangeRef.current ]);

  return (
    <Wrapper>
      <FullRange ref={fullRangeRef}>
        <LowerPriceEstimatedRange width={percentageToWidth(LOWER_PRICES_WIDTH)} isPriceInCurrentRange={priceType === PriceOverviewType.Low} />
        <HigherPriceEstimatedRange width={percentageToWidth(HIGHER_PRICES_WIDTH)} isPriceInCurrentRange={priceType === PriceOverviewType.High} />
        <EstimatedRange left={percentageToWidth(LOWER_PRICES_WIDTH)} width={percentageToWidth(ESTIMATED_WIDTH)} gradientPoint={percentageToWidth(estimatedPositionInBlue)}/>
        <EstimatedPoint left={percentageToWidth(ESTIMATED_WIDTH)} />
        {record ? (
          <ChartMarkerContainer left={percentageToWidth(markerPosition(record.price))}>
            <Tooltip
              isSpecialColor
              trianglePosition={trianglePosition}
              tooltipOffset={tooltipOffset}
            >
              <SmallText style={{ whiteSpace: 'nowrap' }}>
                {t('widget.prices.estimation.recordTooltip', {
                  type: record.type,
                  amount: fm(record.price, 2, getEnforcedSiValue(record.price)),
                  priceOverviewType: priceType,
                })}
              </SmallText>
            </Tooltip>
            <CurrentBulletinMarker/>
          </ChartMarkerContainer>
        ) : null}
      </FullRange>
      <Labels>
        <SmallText style={{ left: percentageToWidth(LOWER_PRICES_WIDTH) }}>
          {fm(minEstimation, 2, getEnforcedSiValue(minEstimation))}
        </SmallText>
        <MiddleLabel style={{ left: percentageToWidth(ESTIMATED_WIDTH) }}>
          <SmallText weight="bold">
            {t('widget.prices.chart.estimate')}
          </SmallText>
          <SmallText weight="bold">
            {fm(estimation, 2, getEnforcedSiValue(estimation))}
          </SmallText>
        </MiddleLabel>
        <SmallText style={{ left: percentageToWidth(LOWER_PRICES_WIDTH + ESTIMATED_WIDTH) }}>
          {fm(maxEstimation, 2, getEnforcedSiValue(maxEstimation))}
        </SmallText>
      </Labels>
      <SpacingBlock mTop={hideAdditionalInfo ? 6 : 9}>
        {!hideAdditionalInfo ? (
          <AdditionalInfoList>
            <AdditionalInfoItem>
              {t('widget.prices.estimation.priceOverview', { type: priceType })}
            </AdditionalInfoItem>
            <AdditionalInfoItem>
              {t('widget.prices.estimation.usualPricesRange', {
                minPrice: fm(minEstimation, 2, getEnforcedSiValue(minEstimation)),
                maxPrice: fm(maxEstimation, 2, getEnforcedSiValue(maxEstimation)),
              })}
            </AdditionalInfoItem>
            <AdditionalInfoItem>
              {t('widget.prices.estimation.localizeEstimatedPrice', { estimatedPrice: fm(estimation, 2, getEnforcedSiValue(estimation)) })}
            </AdditionalInfoItem>
          </AdditionalInfoList>
        ) : null}
      </SpacingBlock>
    </Wrapper>
  );
};

export default Chart;
